import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { 
  ArrowLeft, ArrowRight, Check, Building2, Calendar, FileText, Package,
  Upload, X, Plus, Trash2, User, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { api, apiMultipart } from '../utils/api';

const DOCUMENT_CATEGORIES = [
  { key: 'architectural', label: 'Architectural Drawings', required: false },
  { key: 'structural', label: 'Structural Drawings', required: false },
  { key: 'working', label: 'Working Drawings', required: false },
  { key: 'plumbing_electrical', label: 'Plumbing & Electrical Drawings', required: false },
  { key: '2d_elevations', label: '2D Elevations', required: false },
  { key: '3d_elevations', label: '3D Elevations', required: false },
  { key: 'landscape', label: 'Landscape Drawings', required: false },
  { key: 'sanction_plan', label: 'Sanction Plan', required: false },
  { key: 'design_quotation', label: 'Design Quotation', required: false },
  { key: 'liaising_quotation', label: 'Liaising Quotation', required: false },
  { key: 'construction_quotation', label: 'Construction Quotation', required: false },
  { key: 'construction_agreement', label: 'Construction Agreement', required: false },
  { key: 'other', label: 'Other Documents', required: false }
];

const DEFAULT_ACTIVITIES = [
  { name: 'Excavation & Foundation', duration: 25, payment_pct: 10, required: true },
  { name: 'Plinth Beam & Column', duration: 20, payment_pct: 15, required: true },
  { name: 'RCC Slab Work', duration: 15, payment_pct: 20, required: true },
  { name: 'Masonry & Brickwork', duration: 20, payment_pct: 15, required: true },
  { name: 'Roofing & Waterproofing', duration: 10, payment_pct: 5, required: true },
  { name: 'Plumbing & Electrical Rough-in', duration: 12, payment_pct: 8, required: true },
  { name: 'Plastering', duration: 15, payment_pct: 10, required: true },
  { name: 'Flooring & Tiling', duration: 15, payment_pct: 10, required: true },
  { name: 'Carpentry & Doors/Windows', duration: 20, payment_pct: 8, required: true },
  { name: 'Painting & Finishing', duration: 12, payment_pct: 7, required: true },
  { name: 'Electrical & Plumbing Fixtures', duration: 7, payment_pct: 5, required: true },
  { name: 'Final Inspection & Handover', duration: 7, payment_pct: 2, required: true }
];

const OPTIONAL_ACTIVITIES = [
  { key: 'swimming_pool', name: 'Swimming Pool Development', duration: 30, payment_pct: 0 },
  { key: 'interiors', name: 'Interior Design & Furnishing', duration: 20, payment_pct: 0 },
  { key: 'landscaping', name: 'Landscaping & Garden', duration: 15, payment_pct: 0 },
  { key: 'compound_wall', name: 'Compound Wall & Gate', duration: 10, payment_pct: 0 }
];

const STEPS = [
  { id: 1, title: 'Basic Info', icon: Building2 },
  { id: 2, title: 'Activities', icon: Calendar },
  { id: 3, title: 'Documents', icon: FileText },
  { id: 4, title: 'Materials', icon: Package }
];

const CreateProjectWizard = ({ open, onClose, onSuccess, customers, engineers }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [creating, setCreating] = useState(false);
  
  // Step 1: Basic Info
  const [basicInfo, setBasicInfo] = useState({
    customer_id: '',
    project_name: '',
    location: '',
    built_up_area: '',
    contract_value: '',
    start_date: '',
    expected_completion_date: '',
    assigned_engineers: []
  });

  // Step 2: Activities
  const [activities, setActivities] = useState([]);
  const [selectedOptional, setSelectedOptional] = useState([]);

  // Step 3: Documents
  const [documents, setDocuments] = useState({});

  // Step 4: Materials
  const [materials, setMaterials] = useState([]);

  // Initialize activities when start date changes
  useEffect(() => {
    if (basicInfo.start_date) {
      initializeActivities();
    }
  }, [basicInfo.start_date, selectedOptional]);

  const initializeActivities = () => {
    const startDate = new Date(basicInfo.start_date);
    let currentDate = new Date(startDate);
    
    const allActivities = [
      ...DEFAULT_ACTIVITIES,
      ...OPTIONAL_ACTIVITIES.filter(a => selectedOptional.includes(a.key))
    ];

    const newActivities = allActivities.map((activity, index) => {
      const activityStart = new Date(currentDate);
      const activityEnd = new Date(currentDate);
      activityEnd.setDate(activityEnd.getDate() + activity.duration);
      
      const result = {
        id: `activity_${index}`,
        name: activity.name,
        start_date: activityStart.toISOString().split('T')[0],
        end_date: activityEnd.toISOString().split('T')[0],
        payment_percentage: activity.payment_pct,
        required: activity.required !== false
      };
      
      currentDate = new Date(activityEnd);
      currentDate.setDate(currentDate.getDate() + 1);
      
      return result;
    });

    setActivities(newActivities);
  };

  const handleBasicInfoChange = (field, value) => {
    setBasicInfo(prev => ({ ...prev, [field]: value }));
  };

  const toggleEngineer = (engineerId) => {
    setBasicInfo(prev => ({
      ...prev,
      assigned_engineers: prev.assigned_engineers.includes(engineerId)
        ? prev.assigned_engineers.filter(id => id !== engineerId)
        : [...prev.assigned_engineers, engineerId]
    }));
  };

  const toggleOptionalActivity = (key) => {
    setSelectedOptional(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const updateActivity = (index, field, value) => {
    setActivities(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleDocumentUpload = (category, files) => {
    setDocuments(prev => ({
      ...prev,
      [category]: [...(prev[category] || []), ...Array.from(files)]
    }));
  };

  const removeDocument = (category, index) => {
    setDocuments(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  const addMaterial = () => {
    setMaterials(prev => [...prev, {
      id: `mat_${Date.now()}`,
      name: '',
      category: '',
      brand: '',
      model: '',
      estimated_cost: ''
    }]);
  };

  const updateMaterial = (index, field, value) => {
    setMaterials(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeMaterial = (index) => {
    setMaterials(prev => prev.filter((_, i) => i !== index));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!basicInfo.customer_id) {
          toast.error('Please select a customer');
          return false;
        }
        if (!basicInfo.project_name) {
          toast.error('Please enter project name');
          return false;
        }
        if (!basicInfo.location) {
          toast.error('Please enter location');
          return false;
        }
        if (!basicInfo.built_up_area) {
          toast.error('Please enter built-up area');
          return false;
        }
        if (!basicInfo.contract_value) {
          toast.error('Please enter contract value');
          return false;
        }
        if (!basicInfo.start_date) {
          toast.error('Please select start date');
          return false;
        }
        if (!basicInfo.expected_completion_date) {
          toast.error('Please select expected completion date');
          return false;
        }
        if (basicInfo.assigned_engineers.length === 0) {
          toast.error('Please assign at least one engineer');
          return false;
        }
        return true;
      case 2:
        return true; // Activities are auto-generated
      case 3:
        return true; // Documents are optional
      case 4:
        return true; // Materials are optional
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setCreating(true);
    
    try {
      const selectedCustomer = customers.find(c => c.user_id === basicInfo.customer_id);
      
      // Create project with activities
      const projectData = {
        customer_id: basicInfo.customer_id,
        customer_name: selectedCustomer?.name || '',
        project_name: basicInfo.project_name,
        location: basicInfo.location,
        built_up_area: parseFloat(basicInfo.built_up_area),
        contract_value: parseFloat(basicInfo.contract_value),
        start_date: basicInfo.start_date,
        expected_completion_date: basicInfo.expected_completion_date,
        assigned_engineers: basicInfo.assigned_engineers,
        optional_activities: selectedOptional,
        activities: activities.map(a => ({
          name: a.name,
          start_date: a.start_date,
          end_date: a.end_date,
          payment_percentage: parseFloat(a.payment_percentage) || 0
        })),
        materials: materials.filter(m => m.name).map(m => ({
          name: m.name,
          category: m.category,
          brand: m.brand,
          model: m.model,
          estimated_cost: parseFloat(m.estimated_cost) || 0
        }))
      };

      const projectRes = await api.post('/projects/create-with-details', projectData);
      const projectId = projectRes.data.project_id;

      // Upload documents
      for (const [category, files] of Object.entries(documents)) {
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('project_id', projectId);
          formData.append('category', category);
          formData.append('name', file.name);
          
          await apiMultipart.post('/documents/upload', formData);
        }
      }

      toast.success('Project created successfully with all details');
      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setBasicInfo({
      customer_id: '',
      project_name: '',
      location: '',
      built_up_area: '',
      contract_value: '',
      start_date: '',
      expected_completion_date: '',
      assigned_engineers: []
    });
    setActivities([]);
    setSelectedOptional([]);
    setDocuments({});
    setMaterials([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const renderStepIndicator = () => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        {STEPS.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
              currentStep > step.id 
                ? 'bg-green-600 border-green-600 text-white'
                : currentStep === step.id
                  ? 'bg-stone-900 border-stone-900 text-white'
                  : 'bg-white border-stone-300 text-stone-400'
            }`}>
              {currentStep > step.id ? (
                <Check className="h-5 w-5" />
              ) : (
                <step.icon className="h-5 w-5" />
              )}
            </div>
            {index < STEPS.length - 1 && (
              <div className={`w-16 h-0.5 mx-2 ${
                currentStep > step.id ? 'bg-green-600' : 'bg-stone-200'
              }`} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs text-stone-600">
        {STEPS.map(step => (
          <span key={step.id} className={currentStep === step.id ? 'font-medium text-stone-900' : ''}>
            {step.title}
          </span>
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Customer Selection */}
        <div className="space-y-2">
          <Label className="flex items-center">
            <User className="h-4 w-4 mr-2 text-stone-400" />
            Customer *
          </Label>
          <Select
            value={basicInfo.customer_id}
            onValueChange={(value) => handleBasicInfoChange('customer_id', value)}
          >
            <SelectTrigger data-testid="customer-select">
              <SelectValue placeholder="Select customer" />
            </SelectTrigger>
            <SelectContent>
              {customers.map(customer => (
                <SelectItem key={customer.user_id} value={customer.user_id}>
                  {customer.name} ({customer.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Project Name */}
        <div className="space-y-2">
          <Label>Project Name *</Label>
          <Input
            value={basicInfo.project_name}
            onChange={(e) => handleBasicInfoChange('project_name', e.target.value)}
            placeholder="Villa Construction - Phase 1"
            data-testid="project-name-input"
          />
        </div>

        {/* Location */}
        <div className="col-span-2 space-y-2">
          <Label>Location *</Label>
          <Input
            value={basicInfo.location}
            onChange={(e) => handleBasicInfoChange('location', e.target.value)}
            placeholder="Plot No. 123, Whitefield, Bangalore"
            data-testid="location-input"
          />
        </div>

        {/* Built-up Area */}
        <div className="space-y-2">
          <Label>Built-up Area (sq.ft) *</Label>
          <Input
            type="number"
            value={basicInfo.built_up_area}
            onChange={(e) => handleBasicInfoChange('built_up_area', e.target.value)}
            placeholder="2500"
            data-testid="area-input"
          />
        </div>

        {/* Contract Value */}
        <div className="space-y-2">
          <Label>Contract Value (₹) *</Label>
          <Input
            type="number"
            value={basicInfo.contract_value}
            onChange={(e) => handleBasicInfoChange('contract_value', e.target.value)}
            placeholder="5000000"
            data-testid="contract-input"
          />
        </div>

        {/* Start Date */}
        <div className="space-y-2">
          <Label>Start Date *</Label>
          <Input
            type="date"
            value={basicInfo.start_date}
            onChange={(e) => handleBasicInfoChange('start_date', e.target.value)}
            data-testid="start-date-input"
          />
        </div>

        {/* Expected Completion */}
        <div className="space-y-2">
          <Label>Expected Completion *</Label>
          <Input
            type="date"
            value={basicInfo.expected_completion_date}
            onChange={(e) => handleBasicInfoChange('expected_completion_date', e.target.value)}
            data-testid="end-date-input"
          />
        </div>
      </div>

      {/* Engineer Assignment */}
      <div className="space-y-3 p-4 bg-stone-50 border border-stone-200 rounded-lg">
        <Label className="flex items-center text-base font-medium">
          <Users className="h-4 w-4 mr-2 text-stone-500" />
          Assign Engineers * <span className="text-xs text-stone-500 ml-2">(Select at least one)</span>
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {engineers.map(engineer => (
            <label 
              key={engineer.user_id} 
              className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                basicInfo.assigned_engineers.includes(engineer.user_id)
                  ? 'bg-stone-900 border-stone-900 text-white'
                  : 'bg-white border-stone-200 hover:border-stone-300'
              }`}
            >
              <Checkbox
                checked={basicInfo.assigned_engineers.includes(engineer.user_id)}
                onCheckedChange={() => toggleEngineer(engineer.user_id)}
                className="border-stone-400"
              />
              <div>
                <p className="font-medium text-sm">{engineer.name}</p>
                <p className={`text-xs ${basicInfo.assigned_engineers.includes(engineer.user_id) ? 'text-stone-300' : 'text-stone-500'}`}>
                  {engineer.email}
                </p>
              </div>
            </label>
          ))}
        </div>
        {engineers.length === 0 && (
          <p className="text-sm text-stone-500 text-center py-4">
            No engineers available. Please add engineers first.
          </p>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      {/* Optional Activities */}
      <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Label className="text-base font-medium text-blue-900">Optional Activities</Label>
        <p className="text-sm text-blue-700 mb-3">Select additional activities to include in the timeline</p>
        <div className="grid grid-cols-2 gap-2">
          {OPTIONAL_ACTIVITIES.map(activity => (
            <label key={activity.key} className="flex items-center space-x-3 cursor-pointer">
              <Checkbox
                checked={selectedOptional.includes(activity.key)}
                onCheckedChange={() => toggleOptionalActivity(activity.key)}
              />
              <span className="text-sm text-blue-900">{activity.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Activities List */}
      <div className="space-y-3">
        <Label className="text-base font-medium">Project Activities Timeline</Label>
        <div className="max-h-[400px] overflow-y-auto space-y-2 pr-2">
          {activities.map((activity, index) => (
            <div key={activity.id} className="p-4 bg-white border border-stone-200 rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="w-6 h-6 bg-stone-100 text-stone-600 rounded-full flex items-center justify-center text-xs font-medium">
                    {index + 1}
                  </span>
                  <span className="font-medium text-stone-900">{activity.name}</span>
                </div>
                {!activity.required && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Optional</span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Start Date</Label>
                  <Input
                    type="date"
                    value={activity.start_date}
                    onChange={(e) => updateActivity(index, 'start_date', e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">End Date</Label>
                  <Input
                    type="date"
                    value={activity.end_date}
                    onChange={(e) => updateActivity(index, 'end_date', e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Payment %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={activity.payment_percentage}
                    onChange={(e) => updateActivity(index, 'payment_percentage', e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        {activities.length === 0 && (
          <p className="text-sm text-stone-500 text-center py-8">
            Please set a start date in Step 1 to generate activities
          </p>
        )}
      </div>

      {/* Total Payment Percentage */}
      {activities.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-stone-100 rounded-lg">
          <span className="text-sm font-medium text-stone-700">Total Payment Allocation</span>
          <span className={`text-lg font-bold ${
            activities.reduce((sum, a) => sum + (parseFloat(a.payment_percentage) || 0), 0) === 100
              ? 'text-green-600'
              : 'text-orange-600'
          }`}>
            {activities.reduce((sum, a) => sum + (parseFloat(a.payment_percentage) || 0), 0)}%
          </span>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <p className="text-sm text-stone-600">
        Upload project documents. Multiple files can be uploaded for each category.
      </p>
      
      <div className="max-h-[450px] overflow-y-auto space-y-3 pr-2">
        {DOCUMENT_CATEGORIES.map(category => (
          <div key={category.key} className="p-4 bg-white border border-stone-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <Label className="font-medium text-stone-900">{category.label}</Label>
              <label className="cursor-pointer">
                <Input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleDocumentUpload(category.key, e.target.files)}
                  accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf"
                />
                <span className="inline-flex items-center px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm rounded-lg transition-colors">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </span>
              </label>
            </div>
            
            {documents[category.key]?.length > 0 && (
              <div className="space-y-2">
                {documents[category.key].map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-stone-50 rounded">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-stone-500" />
                      <span className="text-sm text-stone-700 truncate max-w-[200px]">{file.name}</span>
                      <span className="text-xs text-stone-500">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDocument(category.key, index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-base font-medium">Pre-Approved Materials</Label>
          <p className="text-sm text-stone-500">Add materials approved at the time of agreement (optional)</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addMaterial}
          className="rounded-lg"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Material
        </Button>
      </div>

      <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2">
        {materials.length === 0 ? (
          <div className="text-center py-12 bg-stone-50 rounded-lg border-2 border-dashed border-stone-200">
            <Package className="h-12 w-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No materials added yet</p>
            <p className="text-sm text-stone-400">Click &quot;Add Material&quot; to include pre-approved materials</p>
          </div>
        ) : (
          materials.map((material, index) => (
            <div key={material.id} className="p-4 bg-white border border-stone-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-stone-700">Material #{index + 1}</span>
                <button
                  type="button"
                  onClick={() => removeMaterial(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Material Name</Label>
                  <Input
                    value={material.name}
                    onChange={(e) => updateMaterial(index, 'name', e.target.value)}
                    placeholder="e.g., Cement"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Category</Label>
                  <Input
                    value={material.category}
                    onChange={(e) => updateMaterial(index, 'category', e.target.value)}
                    placeholder="e.g., Construction"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Brand</Label>
                  <Input
                    value={material.brand}
                    onChange={(e) => updateMaterial(index, 'brand', e.target.value)}
                    placeholder="e.g., UltraTech"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Model</Label>
                  <Input
                    value={material.model}
                    onChange={(e) => updateMaterial(index, 'model', e.target.value)}
                    placeholder="e.g., PPC Grade"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs text-stone-500">Estimated Cost (₹)</Label>
                  <Input
                    type="number"
                    value={material.estimated_cost}
                    onChange={(e) => updateMaterial(index, 'estimated_cost', e.target.value)}
                    placeholder="0"
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <DialogTitle className="text-xl font-semibold">Create New Project</DialogTitle>
          <DialogDescription>Step {currentStep} of 4: {STEPS[currentStep - 1].title}</DialogDescription>
        </DialogHeader>

        {renderStepIndicator()}

        <div className="flex-1 overflow-y-auto py-4" data-testid="create-project-wizard">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t border-stone-100">
          <Button
            type="button"
            variant="outline"
            onClick={currentStep === 1 ? handleClose : prevStep}
            className="rounded-lg"
          >
            {currentStep === 1 ? (
              'Cancel'
            ) : (
              <>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </>
            )}
          </Button>

          {currentStep < 4 ? (
            <Button
              type="button"
              onClick={nextStep}
              className="bg-stone-900 hover:bg-stone-800 text-white rounded-lg"
              data-testid="next-step-button"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={creating}
              className="bg-green-600 hover:bg-green-700 text-white rounded-lg"
              data-testid="create-project-submit"
            >
              {creating ? (
                <span className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Creating...
                </span>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Create Project
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProjectWizard;
